import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (msg.length > 500) return;
    setSubmitting(true);
    const data = new URLSearchParams();
    Array.from(new FormData(form)).forEach(([k, v]) => data.append(k, v as string));
    try {
      await fetch('https://readdy.ai/api/form/d77s8oj86jhav3jpetv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Header */}
        <section className="py-20 text-center" style={{ backgroundColor: '#F5F0E8' }}>
          <div className="max-w-2xl mx-auto px-4">
            <span className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full" style={{ backgroundColor: '#EDE5D8' }}>
              <i className="ri-mail-send-line text-2xl" style={{ color: '#C9933A' }}></i>
            </span>
            <h1 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-5xl font-black mb-4">Let&apos;s Connect</h1>
            <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42', lineHeight: 1.8 }} className="text-lg">
              Questions? Custom orders? Just want to chat about the perfect t-shirt? We&apos;re real humans who love hearing from you.
            </p>
          </div>
        </section>

        {/* Two-column layout */}
        <section className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
          <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Left: Contact Info */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-2xl font-bold">Reach Us Directly</h2>
              {[
                { icon: 'ri-phone-line', label: 'Call Us', value: '734.646.8620', href: 'tel:7346468620' },
                { icon: 'ri-mail-line', label: 'Email Us', value: 'earthwisdom80@gmail.com', href: 'mailto:earthwisdom80@gmail.com' },
                { icon: 'ri-facebook-fill', label: 'Find Us on Facebook', value: 'EarthWisdomMusicandClothing', href: 'https://www.facebook.com/EarthWisdomMusicandClothing' },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel={item.href.startsWith('http') ? 'nofollow noopener noreferrer' : undefined}
                  className="flex items-center gap-4 p-5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
                  style={{ backgroundColor: '#EDE5D8' }}
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#1E4F4F' }}>
                    <i className={`${item.icon} text-xl`} style={{ color: '#E8B96A' }}></i>
                  </span>
                  <div>
                    <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{item.label}</p>
                    <p style={{ fontFamily: '"Lato", sans-serif', color: '#4A3728', fontWeight: 700 }} className="text-base">{item.value}</p>
                  </div>
                </a>
              ))}
              <div className="mt-4 p-6 rounded-xl" style={{ backgroundColor: '#1E4F4F' }}>
                <p style={{ fontFamily: '"Dancing Script", cursive', color: '#E8B96A', fontSize: '1.2rem' }} className="mb-2">Based in Michigan</p>
                <p style={{ fontFamily: '"Lato", sans-serif', color: '#A0C8C8', lineHeight: 1.7 }} className="text-sm">
                  Earth Wisdom™ Music and Clothing has been serving customers since 1981. We typically respond within 24 hours.
                </p>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-2xl font-bold mb-6">Send Us a Message</h2>
              {submitted ? (
                <div className="rounded-2xl p-12 text-center" style={{ backgroundColor: '#EDE5D8' }}>
                  <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full" style={{ backgroundColor: '#1E4F4F' }}>
                    <i className="ri-check-line text-3xl" style={{ color: '#E8B96A' }}></i>
                  </span>
                  <h3 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-2xl font-bold mb-2">Message Sent!</h3>
                  <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42' }}>Thank you for reaching out. We&apos;ll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label style={{ fontFamily: '"Lato", sans-serif', color: '#4A3728', fontSize: '0.85rem', fontWeight: 700 }}>Your Name *</label>
                      <input name="name" type="text" required placeholder="Jane Smith" className="px-4 py-3 rounded-lg border-2 text-sm outline-none transition-colors focus:border-[#C9933A]" style={{ borderColor: '#EDE5D8', backgroundColor: '#fff', fontFamily: '"Lato", sans-serif', color: '#4A3728' }} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label style={{ fontFamily: '"Lato", sans-serif', color: '#4A3728', fontSize: '0.85rem', fontWeight: 700 }}>Email Address *</label>
                      <input name="email" type="email" required placeholder="jane@example.com" className="px-4 py-3 rounded-lg border-2 text-sm outline-none transition-colors focus:border-[#C9933A]" style={{ borderColor: '#EDE5D8', backgroundColor: '#fff', fontFamily: '"Lato", sans-serif', color: '#4A3728' }} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label style={{ fontFamily: '"Lato", sans-serif', color: '#4A3728', fontSize: '0.85rem', fontWeight: 700 }}>Phone (optional)</label>
                    <input name="phone" type="tel" placeholder="734.000.0000" className="px-4 py-3 rounded-lg border-2 text-sm outline-none transition-colors focus:border-[#C9933A]" style={{ borderColor: '#EDE5D8', backgroundColor: '#fff', fontFamily: '"Lato", sans-serif', color: '#4A3728' }} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label style={{ fontFamily: '"Lato", sans-serif', color: '#4A3728', fontSize: '0.85rem', fontWeight: 700 }}>Subject *</label>
                    <select name="subject" required className="px-4 py-3 rounded-lg border-2 text-sm outline-none transition-colors focus:border-[#C9933A] cursor-pointer" style={{ borderColor: '#EDE5D8', backgroundColor: '#fff', fontFamily: '"Lato", sans-serif', color: '#4A3728' }}>
                      <option value="">Select a subject...</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Product Question">Product Question</option>
                      <option value="Magic Dress">Magic Dress</option>
                      <option value="T-Shirts">T-Shirts</option>
                      <option value="Jackets & Ruanas">Jackets &amp; Ruanas</option>
                      <option value="Custom Order">Custom Order</option>
                      <option value="Wholesale">Wholesale</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label style={{ fontFamily: '"Lato", sans-serif', color: '#4A3728', fontSize: '0.85rem', fontWeight: 700 }}>
                      Message * <span style={{ color: charCount > 500 ? '#c0392b' : '#7A5C42', fontWeight: 400 }}>({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us what you're looking for..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="px-4 py-3 rounded-lg border-2 text-sm outline-none transition-colors focus:border-[#C9933A] resize-none"
                      style={{ borderColor: '#EDE5D8', backgroundColor: '#fff', fontFamily: '"Lato", sans-serif', color: '#4A3728' }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || charCount > 500}
                    className="w-full py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                    style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif', letterSpacing: '0.1em' }}
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                  <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42', fontSize: '0.75rem', textAlign: 'center' }}>
                    We typically respond within 24 hours. Free shipping on orders $75+.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Trust Footer Strip */}
        <section className="py-10 text-center" style={{ backgroundColor: '#EDE5D8' }}>
          <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42', fontSize: '0.9rem' }}>
            Trusted since 1981 &nbsp;✦&nbsp; Based in Michigan &nbsp;✦&nbsp; Artist-Original Designs &nbsp;✦&nbsp; Plus-Size Inclusive
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
